import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Play, Upload as UploadIcon } from 'lucide-react';

import Modal from '@/components/Common/Modal';
import { Button } from '@/components/Common/button';
import ProgressStepper, { ProgressStep } from '@/components/Common/ProgressStepper';
import organizationImportService, {
  ImportEvent,
  ImportValidationReport,
} from '@/services/organizationImportService';

import StepUpload from './steps/StepUpload';
import StepValidation from './steps/StepValidation';
import StepConfirm from './steps/StepConfirm';
import StepProgress from './steps/StepProgress';
import type { ProgressEntry, WizardStep } from './types';

interface ImportOrganizationWizardProps {
  open: boolean;
  onClose: () => void;
  onImported?: (organizationId: string) => void;
}

const STEP_ORDER: WizardStep[] = ['upload', 'validate', 'confirm', 'progress', 'complete'];

export const ImportOrganizationWizard: React.FC<ImportOrganizationWizardProps> = ({
  open,
  onClose,
  onImported,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<WizardStep>('upload');
  const [file, setFile] = useState<File | null>(null);

  // validation state
  const [validating, setValidating] = useState(false);
  const [report, setReport] = useState<ImportValidationReport | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // import state
  const [running, setRunning] = useState(false);
  const [entries, setEntries] = useState<ProgressEntry[]>([]);
  const [finalError, setFinalError] = useState<string | null>(null);
  const [createdOrgId, setCreatedOrgId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const redirectTimerRef = useRef<number | null>(null);

  const stepIndex = STEP_ORDER.indexOf(step);

  const stepperSteps: ProgressStep[] = useMemo(
    () => [
      { id: 'upload', label: t('organizations.import.steps.upload', 'Fichier') },
      { id: 'validate', label: t('organizations.import.steps.validate', 'Validation') },
      { id: 'confirm', label: t('organizations.import.steps.confirm', 'Confirmation') },
      { id: 'progress', label: t('organizations.import.steps.progress', 'Import') },
    ],
    [t],
  );

  // Reset everything when the modal closes
  useEffect(() => {
    if (!open) {
      setStep('upload');
      setFile(null);
      setReport(null);
      setValidationError(null);
      setValidating(false);
      setRunning(false);
      setEntries([]);
      setFinalError(null);
      setCreatedOrgId(null);
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (redirectTimerRef.current) window.clearTimeout(redirectTimerRef.current);
    };
  }, []);

  const handleFileSelected = (selected: File) => {
    setFile(selected);
    setReport(null);
    setValidationError(null);
  };

  const goToValidate = async () => {
    if (!file) return;
    setStep('validate');
    setValidating(true);
    setReport(null);
    setValidationError(null);
    try {
      const result = await organizationImportService.validate(file);
      setReport(result);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setValidationError(
        detail ||
          (err as Error)?.message ||
          t('organizations.import.validate.unknownError', 'Erreur inconnue'),
      );
    } finally {
      setValidating(false);
    }
  };

  const startImport = async () => {
    if (!file) return;
    setStep('progress');
    setRunning(true);
    setEntries([]);
    setFinalError(null);
    setCreatedOrgId(null);

    abortRef.current = new AbortController();
    try {
      const result = await organizationImportService.commit(
        file,
        (evt) => handleStreamEvent(evt),
        abortRef.current.signal,
      );
      setCreatedOrgId(result.organizationId);
      setStep('complete');
      onImported?.(result.organizationId);
      // Auto-redirect after 2.5s; the user can also click the button.
      redirectTimerRef.current = window.setTimeout(() => {
        navigate(`/organizations/${result.organizationId}`);
        onClose();
      }, 2500);
    } catch (err) {
      const message = (err as Error)?.message || t('organizations.import.progress.unknownError', 'Erreur inconnue');
      setFinalError(message);
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const handleStreamEvent = (evt: ImportEvent) => {
    setEntries((prev) => mergeEvent(prev, evt, t));
  };

  const handleClose = () => {
    if (running) return;
    onClose();
  };

  const goToConfirm = () => setStep('confirm');
  const goToUpload = () => setStep('upload');

  const goToOrgPage = () => {
    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    if (createdOrgId) {
      navigate(`/organizations/${createdOrgId}`);
    }
    onClose();
  };

  const validationOk = report?.ok === true;

  let footer: React.ReactNode = null;
  if (step === 'upload') {
    footer = (
      <>
        <Button variant="outline" onClick={handleClose}>
          {t('global.cancel', 'Annuler')}
        </Button>
        <Button onClick={goToValidate} disabled={!file} className="gap-2">
          {t('global.next', 'Suivant')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </>
    );
  } else if (step === 'validate') {
    footer = (
      <>
        <Button variant="outline" onClick={goToUpload} disabled={validating} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('global.back', 'Retour')}
        </Button>
        <Button onClick={goToConfirm} disabled={!validationOk || validating} className="gap-2">
          {t('global.next', 'Suivant')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </>
    );
  } else if (step === 'confirm') {
    footer = (
      <>
        <Button variant="outline" onClick={() => setStep('validate')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('global.back', 'Retour')}
        </Button>
        <Button onClick={startImport} className="gap-2">
          <Play className="h-4 w-4" />
          {t('organizations.import.actions.start', 'Lancer l\'import')}
        </Button>
      </>
    );
  } else if (step === 'progress') {
    footer = (
      <>
        <Button variant="outline" onClick={handleClose} disabled={running}>
          {t('global.close', 'Fermer')}
        </Button>
        {finalError && !running && (
          <Button onClick={goToUpload} className="gap-2">
            <UploadIcon className="h-4 w-4" />
            {t('organizations.import.actions.retry', 'Recommencer')}
          </Button>
        )}
      </>
    );
  } else if (step === 'complete') {
    footer = (
      <>
        <Button variant="outline" onClick={handleClose}>
          {t('global.close', 'Fermer')}
        </Button>
        <Button onClick={goToOrgPage} className="gap-2">
          <ArrowRight className="h-4 w-4" />
          {t('organizations.import.actions.openOrganization', 'Ouvrir l\'organisation')}
        </Button>
      </>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      hideDefaultFooter
      hideCloseButton={running}
      className="max-w-2xl"
      title={t('organizations.import.title', 'Importer une organisation')}
      description={t(
        'organizations.import.subtitle',
        'Créer une organisation et toute sa hiérarchie depuis un fichier YAML ou JSON.',
      )}
      bodyClassName="max-h-[70vh] overflow-y-auto"
      footer={footer}
    >
      <div className="space-y-4">
        <ProgressStepper
          steps={stepperSteps}
          currentStep={step === 'complete' ? stepperSteps.length : Math.min(stepIndex, stepperSteps.length - 1)}
          hasError={!!finalError}
        />

        {step === 'upload' && (
          <StepUpload file={file} onFileSelected={handleFileSelected} />
        )}
        {step === 'validate' && (
          <StepValidation report={report} loading={validating} error={validationError} />
        )}
        {step === 'confirm' && report && file && (
          <StepConfirm report={report} fileName={file.name} />
        )}
        {step === 'progress' && (
          <StepProgress entries={entries} running={running} finalError={finalError} />
        )}
        {step === 'complete' && (
          <CompleteSplash
            organizationName={report?.organization_name ?? ''}
            onOpenOrg={goToOrgPage}
          />
        )}
      </div>
    </Modal>
  );
};

const CompleteSplash: React.FC<{ organizationName: string; onOpenOrg: () => void }> = ({
  organizationName,
  onOpenOrg,
}) => {
  const { t } = useTranslation();
  return (
    <div className="py-8 flex flex-col items-center text-center gap-3">
      <CheckCircle2 className="h-12 w-12 text-green-600" />
      <div className="text-base font-semibold">
        {t('organizations.import.complete.title', 'Organisation importée')}
      </div>
      <div className="text-sm text-muted-foreground max-w-sm">
        {organizationName
          ? t('organizations.import.complete.descriptionWithName', {
              defaultValue: '« {{name}} » a été créée. Vous allez être redirigé vers sa page.',
              name: organizationName,
            })
          : t(
              'organizations.import.complete.description',
              'L\'organisation a été créée. Vous allez être redirigé vers sa page.',
            )}
      </div>
      <Button onClick={onOpenOrg} variant="outline" className="mt-2">
        {t('organizations.import.actions.openOrganizationNow', 'Ouvrir maintenant')}
      </Button>
    </div>
  );
};

// ----- Stream event → progress entry merger ------------------------------

function mergeEvent(
  prev: ProgressEntry[],
  evt: ImportEvent,
  t: TFunction,
): ProgressEntry[] {
  const data = evt.data || {};
  const step = String((data as { step?: string }).step ?? '');
  const status = String((data as { status?: string }).status ?? '');

  if (evt.event === 'error') {
    const id = `error-${Date.now()}`;
    return [
      ...prev,
      {
        id,
        label: t('organizations.import.progress.errorLine', "Erreur — annulation"),
        status: 'error',
        detail: String(data.message ?? ''),
      },
    ];
  }
  if (evt.event === 'complete') {
    return [
      ...prev,
      {
        id: 'complete',
        label: t('organizations.import.progress.complete', 'Import terminé'),
        status: 'success',
      },
    ];
  }

  // progress
  switch (step) {
    case 'import':
      if (status === 'starting') {
        const totals = (data as { totals?: { domains?: number; usecases?: number; cadrages?: number } }).totals;
        return [
          ...prev,
          {
            id: 'starting',
            label: t('organizations.import.progress.starting', 'Démarrage de l\'import'),
            status: 'success',
            detail: totals
              ? t('organizations.import.progress.totals', {
                  defaultValue: '{{d}} domaines · {{u}} cas d\'usage · {{c}} cadrages',
                  d: totals.domains,
                  u: totals.usecases,
                  c: totals.cadrages,
                })
              : undefined,
          },
        ];
      }
      return prev;
    case 'organization':
      return upsertEntry(prev, {
        id: 'organization',
        label: t('organizations.import.progress.organization', 'Organisation'),
        status: status === 'created' ? 'success' : 'pending',
        detail: data.name ? String(data.name) : undefined,
      });
    case 'domain': {
      const id = `domain-${data.id}`;
      return upsertEntry(prev, {
        id,
        label: t('organizations.import.progress.domainLine', {
          defaultValue: 'Domaine : {{name}}',
          name: data.name,
        }),
        status: 'success',
      });
    }
    case 'usecase': {
      const id = `usecase-${data.id}`;
      return upsertEntry(prev, {
        id,
        label: t('organizations.import.progress.usecaseLine', {
          defaultValue: 'Cas d\'usage : {{name}}',
          name: data.name,
        }),
        status: 'success',
        detail: data.domain_name ? String(data.domain_name) : undefined,
      });
    }
    case 'cadrage': {
      const id = `cadrage-${data.id}`;
      return upsertEntry(prev, {
        id,
        label: t('organizations.import.progress.cadrageLine', {
          defaultValue: 'Cadrage : {{name}}',
          name: data.name,
        }),
        status: 'success',
        detail: data.usecase_name ? String(data.usecase_name) : undefined,
      });
    }
    case 'commit':
      return upsertEntry(prev, {
        id: 'commit',
        label: t('organizations.import.progress.commit', 'Validation en base'),
        status: 'success',
      });
    case 'sector_template': {
      const lineStatus =
        status === 'applied' ? 'success' : status === 'warning' ? 'warning' : 'pending';
      return upsertEntry(prev, {
        id: 'sector_template',
        label: t('organizations.import.progress.sectorTemplate', 'Configuration secteur'),
        status: lineStatus,
        detail: data.message ? String(data.message) : undefined,
      });
    }
    default:
      return prev;
  }
}

function upsertEntry(prev: ProgressEntry[], entry: ProgressEntry): ProgressEntry[] {
  const idx = prev.findIndex((p) => p.id === entry.id);
  if (idx === -1) return [...prev, entry];
  const next = [...prev];
  next[idx] = { ...next[idx], ...entry };
  return next;
}

export default ImportOrganizationWizard;
